import { User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import Button from "../../ui/Button";
import Logo from "../../ui/Logo";
import useDesign from "../../useDesign";
import { checkoutHref, type LandingData } from "../types";

export type NavLink = { href: "#features" | "#plans" | "#payment" | "#faq"; label: string };

// Landing top bar: transparent at the top, glass once scrolled; the compact buy button appears after the hero's own CTA has scrolled away.
export default function LandingNav({
  data,
  links = [],
  heroId,
  overlay = false,
  width = "max-w-[1200px]",
}: {
  data: Pick<LandingData, "loggedIn" | "proDaysLeft">;
  links?: NavLink[];
  heroId?: string;
  overlay?: boolean;
  width?: string;
}) {
  const { t } = useTranslation();
  const design = useDesign();
  const sentinel = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [showCta, setShowCta] = useState(!heroId);

  useEffect(() => {
    const top = sentinel.current;
    const hero = heroId ? document.getElementById(heroId) : null;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === top) setScrolled(!e.isIntersecting);
        // only once the hero has left through the top, not while it is still below the fold
        else setShowCta(!e.isIntersecting && e.boundingClientRect.top < 0);
      }
    });
    if (top) io.observe(top);
    if (hero) io.observe(hero);
    else setShowCta(true);
    return () => io.disconnect();
  }, [heroId]);

  const ctaLabel = data.proDaysLeft !== null ? t("ui.landing.hero.ctaRenew") : t("ui.landing.hero.cta");

  return (
    <>
      {/* 8px tall marker at the very top of the page; once it leaves the viewport the bar turns solid */}
      <div ref={sentinel} aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-2" />
      <header className={cn(overlay ? "fixed inset-x-0" : "sticky", "top-0 z-nav isolate pt-[env(safe-area-inset-top)]")}>
        <div
          aria-hidden
          className={cn(
            "absolute inset-0 -z-10 border-b border-line bg-[color-mix(in_srgb,var(--c-bg)_80%,transparent)] backdrop-blur-xl backdrop-saturate-150",
            "transition-opacity duration-300 ease-out",
            scrolled ? "opacity-100" : "opacity-0",
          )}
        />
        <div className={cn("mx-auto flex h-[var(--nav-h)] w-full items-center gap-2 px-[var(--gutter)] md:gap-6", width)}>
          <Logo size={design === "c" ? 32 : 30} />
          {links.length > 0 && (
            <nav className="hidden items-center gap-1 md:flex">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="inline-flex min-h-11 items-center rounded-btn px-3.5 text-[15px] text-ink-2 transition-colors duration-200 hover:bg-accent-soft hover:text-ink"
                >
                  {l.label}
                </a>
              ))}
            </nav>
          )}
          <div className="ms-auto flex items-center gap-1.5">
            {data.loggedIn ? (
              <Button
                to="/profile"
                prefetch="intent"
                variant={design === "a" ? "ghost" : "secondary"}
                className="w-11 px-0 md:w-auto md:px-4"
                aria-label={t("ui.landing.nav.account")}
              >
                <User className="size-5 md:hidden" strokeWidth={1.75} aria-hidden />
                <span className="hidden md:inline">{t("ui.landing.nav.account")}</span>
              </Button>
            ) : (
              <Button to="/login" prefetch="intent" variant={design === "a" ? "ghost" : "secondary"} className="px-4">
                {t("ui.landing.nav.login")}
              </Button>
            )}
            <Button
              to={checkoutHref()}
              className={cn(
                "px-4 text-sm transition-[opacity,transform,visibility] duration-300 ease-out",
                showCta ? "visible opacity-100" : "invisible -translate-y-1.5 opacity-0",
              )}
              aria-hidden={!showCta}
              tabIndex={showCta ? undefined : -1}
            >
              {ctaLabel}
            </Button>
          </div>
        </div>
      </header>
    </>
  );
}
